import { useEffect, useState } from 'react';
import { Button, Card, Col, InputNumber, Row, Select, Space, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import axios from 'axios';
import FileUploader from './FileUploader';

const { Text } = Typography;

const TEST_CODES = [
  { value: 'AFT',  label: 'AFT — Air Fibre Testing' },
  { value: 'MAT',  label: 'MAT — Mould in Air Testing' },
  { value: 'BAT',  label: 'BAT — Bulk Asbestos Testing' },
  { value: 'BWT',  label: 'BWT — Bacteria in Water Testing' },
  { value: 'OBWT', label: 'OBWT — ODW Bacteria in Water Testing' },
  { value: 'MST',  label: 'MST — Mould Surface Testing' },
  { value: 'PSP',  label: 'PSP — Private Subsidy Program' },
];

let nextId = 1;

function FormEntry({ item, index, formTypes, onRemove, onChange }) {
  return (
    <Card
      className="form-entry-card"
      style={{ marginBottom: 12 }}
      bodyStyle={{ padding: 14 }}
    >
      {/* header */}
      <div className="form-entry-header">
        <span className="form-entry-label">Form {index + 1}</span>
        <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={onRemove}>
          Remove
        </Button>
      </div>

      {/* Row 1: Form Type + Number of Pages */}
      <Row gutter={12} style={{ marginBottom: 12 }}>
        <Col xs={16}>
          <Text strong style={{ display: 'block', marginBottom: 6, fontSize: 13 }}>
            Form Type <span style={{ color: '#ff4d4f' }}>*</span>
          </Text>
          <Select
            placeholder="— Select —"
            value={item.formType || undefined}
            onChange={(val, opt) => onChange({ ...item, formType: val, formLabel: opt?.label ?? '' })}
            style={{ width: '100%' }}
            size="large"
            options={formTypes}
          />
        </Col>
        <Col xs={8}>
          <Text strong style={{ display: 'block', marginBottom: 6, fontSize: 13 }}>
            Pages
          </Text>
          <InputNumber
            min={1} max={100}
            value={item.pageCount}
            onChange={val => onChange({ ...item, pageCount: val || 1 })}
            style={{ width: '100%' }}
            size="large"
          />
        </Col>
      </Row>

      {/* Row 2: Test Code (per form) */}
      <div style={{ marginBottom: 12 }}>
        <Text strong style={{ display: 'block', marginBottom: 6, fontSize: 13 }}>
          Test Code <span style={{ color: '#ff4d4f' }}>*</span>
        </Text>
        <Select
          placeholder="— Select test code —"
          value={item.testCode || undefined}
          onChange={val => onChange({ ...item, testCode: val })}
          style={{ width: '100%' }}
          size="large"
          options={TEST_CODES}
          allowClear
        />
      </div>

      {/* File uploader */}
      <FileUploader
        files={item.files}
        onFilesChange={newFiles => onChange({ ...item, files: newFiles })}
      />
    </Card>
  );
}

export default function BatchBuilder({ items, onChange, apiBase }) {
  const [formTypes, setFormTypes] = useState([]);

  useEffect(() => {
    axios.get(`${apiBase}/form-types`)
      .then(res => {
        const types = res.data.formTypes || [];
        setFormTypes(types.map(ft => ({ value: ft.value, label: ft.label })));
      })
      .catch(() => setFormTypes([]));
  }, [apiBase]);

  function addEntry() {
    onChange([
      ...items,
      { id: nextId++, formType: '', formLabel: '', testCode: '', pageCount: 1, files: [] },
    ]);
  }

  function updateEntry(index, updated) {
    const next = [...items];
    next[index] = updated;
    onChange(next);
  }

  function removeEntry(index) {
    onChange(items.filter((_, i) => i !== index));
  }

  const totalFiles = items.reduce((s, i) => s + i.files.length, 0);
  const totalPages = items.reduce((s, i) => s + i.pageCount, 0);

  return (
    <div>
      {items.map((item, i) => (
        <FormEntry
          key={item.id}
          item={item}
          index={i}
          formTypes={formTypes}
          onRemove={() => removeEntry(i)}
          onChange={updated => updateEntry(i, updated)}
        />
      ))}

      <Button
        type="dashed"
        icon={<PlusOutlined />}
        onClick={addEntry}
        size="large"
        style={{ width: '100%', marginBottom: 12 }}
      >
        Add Form
      </Button>

      {items.length > 0 && (
        <Card size="small" style={{ background: '#fafafa' }}>
          <Space split={<Text type="secondary">·</Text>} wrap>
            <Text><strong>{items.length}</strong> form{items.length !== 1 ? 's' : ''}</Text>
            <Text><strong>{totalFiles}</strong> file{totalFiles !== 1 ? 's' : ''}</Text>
            <Text><strong>{totalPages}</strong> page{totalPages !== 1 ? 's' : ''}</Text>
          </Space>
        </Card>
      )}
    </div>
  );
}